import React, { useEffect, useState, useCallback } from "react";
import { fetchRefreshCalendar } from "../api/client";
import type { RefreshCalendar as RefreshCalendarData, RefreshCalendarDay } from "../types";

interface RefreshCalendarProps {
  datasetId: string;
  datasetName: string;
}

// `in_progress` covers a day whose runs have not resolved — Power BI reports a
// running refresh as "Unknown". Those days used to come back as "healthy",
// because the API counted anything that was not "Failed" as a success.
type CellStatus = 'healthy' | 'partial' | 'failed' | 'in_progress' | 'no_data' | 'sla_breach';

const STATUS_COLORS: Record<CellStatus, string> = {
  healthy: "#22c55e",
  partial: "#f97316",
  failed: "#ef4444",
  in_progress: "#60a5fa",
  no_data: "#d1d5db",
  sla_breach: "#f97316",
};

const STATUS_LABELS: Record<CellStatus, string> = {
  healthy: "Healthy",
  partial: "Partial failure",
  failed: "Failed",
  in_progress: "In progress",
  no_data: "No data",
  sla_breach: "SLA breach",
};

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

interface PopoverInfo {
  day: RefreshCalendarDay;
  x: number;
  y: number;
}

export default function RefreshCalendar({ datasetId, datasetName }: RefreshCalendarProps) {
  // UTC, because the cells are UTC dates: the calendar endpoint buckets by the
  // refresh-history date partition. Opening on the browser's local month meant
  // that around a month boundary the calendar opened on a month the data had
  // not reached yet, showing an empty grid for a dataset that was refreshing
  // normally.
  const now = new Date();
  const [year, setYear] = useState(now.getUTCFullYear());
  const [month, setMonth] = useState(now.getUTCMonth() + 1); // 1-indexed
  const [calendarData, setCalendarData] = useState<RefreshCalendarData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [popover, setPopover] = useState<PopoverInfo | null>(null);

  useEffect(() => {
    if (!popover) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPopover(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [popover]);

  const fetchData = useCallback(() => {
    if (!datasetId) return;
    setLoading(true);
    setError(null);
    fetchRefreshCalendar(datasetId, year, month)
      .then(setCalendarData)
      .catch((err) => setError(err.message ?? "Failed to load calendar data."))
      .finally(() => setLoading(false));
  }, [datasetId, year, month]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  function prevMonth() {
    if (month === 1) {
      setMonth(12);
      setYear((y) => y - 1);
    } else {
      setMonth((m) => m - 1);
    }
  }

  function nextMonth() {
    if (month === 12) {
      setMonth(1);
      setYear((y) => y + 1);
    } else {
      setMonth((m) => m + 1);
    }
  }

  // Build a 6-week grid (7 cols × up to 6 rows)
  function buildGrid(): (RefreshCalendarDay | null)[][] {
    if (!calendarData) return [];

    const firstDay = new Date(year, month - 1, 1).getDay(); // 0=Sun
    const byDate: Record<string, RefreshCalendarDay> = {};
    for (const d of calendarData.days) {
      byDate[d.date] = d;
    }

    const grid: (RefreshCalendarDay | null)[][] = [];
    let week: (RefreshCalendarDay | null)[] = Array(firstDay).fill(null);

    for (const day of calendarData.days) {
      week.push(byDate[day.date] ?? null);
      if (week.length === 7) {
        grid.push(week);
        week = [];
      }
    }
    if (week.length > 0) {
      while (week.length < 7) week.push(null);
      grid.push(week);
    }
    return grid;
  }

  const grid = buildGrid();
  const summary = calendarData?.summary ?? {};

  return (
    <div className="select-none">
      {/* Month navigation */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={prevMonth}
          className="px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm"
        >
          &#8592; Previous
        </button>
        <span className="text-base font-semibold text-gray-800">
          {MONTH_NAMES[month - 1]} {year}
        </span>
        <button
          onClick={nextMonth}
          className="px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm"
        >
          Next &#8594;
        </button>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-10 text-gray-400 text-sm">
          <svg className="w-5 h-5 animate-spin mr-2" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          Loading calendar…
        </div>
      )}

      {error && !loading && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 mb-3">
          {error}
          <button onClick={fetchData} className="ml-3 underline text-xs">Retry</button>
        </div>
      )}

      {!loading && !error && calendarData && (
        <div className="relative">
          {/* Day-of-week header */}
          <div className="grid grid-cols-7 gap-1 mb-1">
            {DAY_NAMES.map((d) => (
              <div key={d} className="text-center text-xs font-medium text-gray-400 py-1">
                {d}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid gap-1">
            {grid.map((week, wi) => (
              <div key={wi} className="grid grid-cols-7 gap-1">
                {week.map((day, di) => {
                  if (!day) {
                    return <div key={di} className="h-10 rounded-md bg-gray-50 opacity-30" />;
                  }
                  const status = day.status as CellStatus;
                  const color = STATUS_COLORS[status] ?? "#d1d5db";
                  const dayNum = parseInt(day.date.slice(8, 10), 10);
                  return (
                    <div
                      key={di}
                      className="h-10 rounded-md flex flex-col items-center justify-center cursor-pointer hover:opacity-80 transition-opacity relative"
                      style={{ backgroundColor: color }}
                      onClick={(e) => {
                        const rect = (e.target as HTMLElement).closest("div")!.getBoundingClientRect();
                        setPopover(
                          popover?.day.date === day.date
                            ? null
                            : { day, x: rect.left + rect.width / 2, y: rect.bottom + 8 }
                        );
                      }}
                    >
                      <span className="text-xs font-semibold text-white drop-shadow">{dayNum}</span>
                      {day.refreshCount > 0 && (
                        <span className="text-[9px] text-white/80 leading-none">{day.refreshCount}×</span>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Popover. Stays a popover rather than becoming a dialog: it is
              three lines of summary deliberately anchored to the day cell that
              was clicked, and a centred overlay would sever that link. It does
              have to be dismissible from the keyboard, though — there was a
              Close button and a click-outside catcher, neither of which a
              keyboard reader can reach past. */}
          {popover && (
            <div
              role="dialog"
              aria-label={`Refresh summary for ${popover.day.date}`}
              className="fixed z-50 bg-white border border-gray-200 rounded-xl shadow-xl p-4 text-sm min-w-[200px] max-w-xs"
              style={{ left: popover.x, top: popover.y, transform: "translateX(-50%)" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="font-semibold text-gray-800 mb-2">{popover.day.date}</div>
              <div className="space-y-1 text-gray-600">
                <div>
                  <span className="font-medium">Status:</span>{" "}
                  <span
                    className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold text-white"
                    style={{ backgroundColor: STATUS_COLORS[popover.day.status as CellStatus] ?? "#6b7280" }}
                  >
                    {STATUS_LABELS[popover.day.status as CellStatus] ?? popover.day.status}
                  </span>
                </div>
                <div><span className="font-medium">Refreshes:</span> {popover.day.refreshCount}</div>
                {popover.day.failureCount > 0 && (
                  <div className="text-red-600"><span className="font-medium">Failures:</span> {popover.day.failureCount}</div>
                )}
              </div>
              <button
                onClick={() => setPopover(null)}
                className="mt-3 text-xs text-gray-400 hover:text-gray-600 underline"
              >
                Close
              </button>
            </div>
          )}

          {/* Click outside to close popover */}
          {popover && (
            <div
              className="fixed inset-0 z-40"
              onClick={() => setPopover(null)}
            />
          )}
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mt-4">
        {(Object.entries(STATUS_COLORS) as [CellStatus, string][]).map(([status, color]) => (
          <div key={status} className="flex items-center gap-1.5 text-xs text-gray-600">
            <span className="w-3 h-3 rounded-sm inline-block" style={{ backgroundColor: color }} />
            {STATUS_LABELS[status]}
          </div>
        ))}
      </div>

      {/* Summary row */}
      {calendarData && (
        <div className="flex flex-wrap gap-4 mt-3 text-xs text-gray-500">
          {Object.entries(summary).map(([key, count]) => (
            <span key={key}>
              <span className="font-medium text-gray-700">{count}</span> {key.replace("_", " ")}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
